import { useMemo } from 'react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import * as Tooltip from '@givebutter/primitives/tooltip';
import { faPlus, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteAppeal as deleteAppealApi, fetchAppeals } from 'api/appeals';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Table from 'components/table';
import { EmptyData } from 'components/util';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useIsPending } from 'customHooks/useIsPending';
import { capitalize } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Appeal } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const MAX_DISPLAYED_CAMPAIGNS = 2;

const columnHelper = createColumnHelper<Appeal>();

const AppealsSettings = () => {
    const history = useHistory();
    const { url: matchUrl } = useRouteMatch();
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();

    const { pagination, setPagination } = Table.useReactTableState();

    const [isPending, params] = useIsPending(
        {
            include: 'defaultCampaigns',
            page: pagination.pageIndex + 1,
        },
        500,
    );

    const { data: appeals } = useSuspenseQuery({
        queryKey: accountQueryKeys.appealsWithFilters(accountId, params),
        queryFn: () => fetchAppeals(accountId, params),
    });

    const { isPending: isDeletingAppeal, mutate: deleteAppeal } = useMutation({
        mutationFn: (appealId: number) => deleteAppealApi(accountId, appealId),
        onSuccess: () => {
            success('Appeal deleted successfully.');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.appeals(accountId),
                type: 'all',
            });
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Could not delete appeal. Please try again.');
        },
    });

    const columns = useMemo(() => {
        return [
            columnHelper.accessor('name', {
                header: 'Appeal name',
                enableSorting: false,
                cell: info => {
                    const appeal = info.row.original;
                    return <p className="max-w-[150px] truncate md:max-w-sm">{appeal.name}</p>;
                },
            }),
            columnHelper.accessor('code', {
                header: 'Appeal code',
                enableSorting: false,
                cell: info => {
                    const appeal = info.row.original;
                    return <p className="max-w-[150px] truncate md:max-w-sm">{appeal.code}</p>;
                },
            }),
            columnHelper.accessor('default_campaigns', {
                header: 'Default campaign',
                enableSorting: false,
                cell: info => {
                    const campaigns = info.row.original.default_campaigns;

                    if (!campaigns?.length) {
                        return <EmptyData />;
                    }

                    const displayedCampaigns = campaigns.slice(0, MAX_DISPLAYED_CAMPAIGNS);
                    const hiddenCampaigns = campaigns.slice(MAX_DISPLAYED_CAMPAIGNS);

                    return (
                        <span>
                            {displayedCampaigns.map((campaign, index) => (
                                <span key={campaign.id}>
                                    {index > 0 && ', '}
                                    <Link to={`/accounts/${accountId}/campaigns/${campaign.id}`}>
                                        {campaign.title}
                                    </Link>
                                </span>
                            ))}
                            {hiddenCampaigns.length > 0 && (
                                <span>
                                    {`, `}
                                    <Tooltip.Root>
                                        <Tooltip.Trigger>
                                            <span>and {hiddenCampaigns.length} more</span>
                                        </Tooltip.Trigger>
                                        <Tooltip.Portal>
                                            <Tooltip.Content>
                                                <Tooltip.Arrow />
                                                <div className="flex flex-col">
                                                    {hiddenCampaigns.map(company => (
                                                        <Link
                                                            key={company.id}
                                                            to={`/accounts/${accountId}/campaigns/${company.id}`}
                                                            className="text-white hover:text-white"
                                                        >
                                                            {company.title}
                                                        </Link>
                                                    ))}
                                                </div>
                                            </Tooltip.Content>
                                        </Tooltip.Portal>
                                    </Tooltip.Root>
                                </span>
                            )}
                        </span>
                    );
                },
            }),
            columnHelper.accessor('raised', {
                header: 'Raised',
                enableSorting: false,
                cell: ({ getValue }) => {
                    return (
                        <>
                            <CurrencySymbol />
                            {getValue()}
                        </>
                    );
                },
            }),
            columnHelper.accessor('status', {
                header: 'Status',
                enableSorting: false,
                cell: ({ getValue }) => {
                    const status = getValue();
                    return (
                        <Badge color={status === 'active' ? 'success' : 'neutral'}>
                            {capitalize(status)}
                        </Badge>
                    );
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const appeal = info.row.original;
                    return (
                        <DropdownMenu.Root key={appeal.id}>
                            <Table.RowDropdownTrigger />
                            <DropdownMenu.Content>
                                <DropdownMenu.LinkItem to={`${matchUrl}/${appeal.id}/edit`}>
                                    Edit appeal
                                </DropdownMenu.LinkItem>
                                <AlertDialog.Root type="error">
                                    <AlertDialog.Trigger asChild>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            Delete appeal
                                        </DropdownMenu.Item>
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Portal>
                                        <AlertDialog.Overlay />
                                        <AlertDialog.Content>
                                            <AlertDialog.Header>
                                                <AlertDialog.Icon />
                                                <AlertDialog.Close />
                                            </AlertDialog.Header>
                                            <AlertDialog.Body>
                                                <AlertDialog.Title>
                                                    Delete appeal {appeal.name}?
                                                </AlertDialog.Title>
                                                <AlertDialog.Description>
                                                    You won't be able to undo this action. Are you
                                                    sure?
                                                </AlertDialog.Description>
                                            </AlertDialog.Body>
                                            <AlertDialog.Footer>
                                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                                <AlertDialog.Action
                                                    onClick={() => {
                                                        deleteAppeal(appeal.id);
                                                    }}
                                                >
                                                    Delete appeal
                                                </AlertDialog.Action>
                                            </AlertDialog.Footer>
                                        </AlertDialog.Content>
                                    </AlertDialog.Portal>
                                </AlertDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ];
    }, [accountId, matchUrl, deleteAppeal]);

    const table = useReactTable({
        data: appeals?.data ?? [],
        columns,
        pageCount: appeals?.meta?.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
    });

    return (
        <div className={'flex flex-col gap-y-8 overflow-x-auto'}>
            <Table.Root isLoading={isDeletingAppeal || isPending} variant="bordered" table={table}>
                <Box>
                    <div className="flex flex-col">
                        <div className="flex justify-between px-6 py-4">
                            <div>
                                <p className="text-2xl font-medium">Appeals</p>
                                <p className="text-sm">
                                    You can use appeals to tag donations and track which appeal
                                    generated a donation.
                                </p>
                            </div>
                            <Link to={`${matchUrl}/new`}>
                                <Button variant="solid" size="sm">
                                    <FontAwesomeIcon icon={faPlus} size="sm" /> Add new
                                </Button>
                            </Link>
                        </div>
                        <Separator />
                        <div className="relative overflow-x-auto">
                            <div className="flex flex-col gap-y-4">
                                <Table.Content>
                                    <Table.Header />
                                    <Table.Body />
                                </Table.Content>
                                <Table.Empty variant={'secondary'}>
                                    <Table.EmptyIcon>
                                        <FontAwesomeIcon icon={faThumbsUp} />
                                    </Table.EmptyIcon>
                                    <Table.EmptyTitle>No appeals yet</Table.EmptyTitle>
                                    <Table.EmptyDescription>
                                        <div className="flex flex-col gap-4">
                                            <div>Create your first appeal.</div>
                                            <Button
                                                variant="solid"
                                                onClick={() =>
                                                    history.push(
                                                        `/accounts/${accountId}/settings/settings/appeals/new`,
                                                    )
                                                }
                                            >
                                                <FontAwesomeIcon icon={faPlus} />
                                                Add appeal
                                            </Button>
                                        </div>
                                    </Table.EmptyDescription>
                                </Table.Empty>
                            </div>
                        </div>
                    </div>
                </Box>
                <Table.Pagination>
                    <Table.PaginationPageNavigation />
                </Table.Pagination>
            </Table.Root>
        </div>
    );
};

export { AppealsSettings };
